import { Injectable } from '@angular/core';

/**
 * Which tapped-notification deep links have already been acted on during this page load.
 *
 * **Why a guard is needed at all.** The screens that receive a deep link (`/menu?event=`,
 * `/menu?reel=1`, `/attractions?code=`) strip the parameter as soon as they act on it, so the guest
 * cannot be bounced back into the same screen by pressing Back. That strip rewrites the CURRENT
 * history entry — and it is not always the one carrying the parameter. Every overlay in this app
 * pushes a synthetic same-URL entry when it opens (`LanguageSheetService`, `ReelService`,
 * `AttractionDetailsSheetService`), so on a first visit to a multilingual venue the language sheet
 * is already sitting on top of the entry splash created, and the strip rewrites the sheet's copy
 * instead. The real `?reel=1` entry survives one step down, and Back replays the notification.
 *
 * Rather than reason about how many synthetic entries happen to be on the stack, the deep link is
 * simply allowed to fire once. A notification is tapped once; acting on it twice is never right.
 *
 * **Why a root-provided singleton.** The same reasoning as `AttractionsEntryService`: the router
 * rebuilds `MenuComponent` on every return to the menu, so a field on the component would forget
 * this the moment the guest backs out of the very screen the link opened. A root-provided
 * singleton's lifetime *is* the page load, which is exactly the right scope — a genuine reload or a
 * fresh scan re-arms it.
 *
 * Keyed by the link itself, so two different notifications in one page load both work.
 */
@Injectable({ providedIn: 'root' })
export class NotificationDeepLinkService {
  private readonly consumed = new Set<string>();

  /** True the first time this exact link is seen in this page load, false every time after. */
  consume(key: string): boolean {
    if (this.consumed.has(key)) return false;
    this.consumed.add(key);
    return true;
  }
}
